import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import NotFoundPage from "../../pages/404Page";
import ActiveOrderDetailPage from "../../pages/ActiveOrderDetailPage";
import ActiveOrdersPage from "../../pages/ActiveOrdersPage";
import AnonymousPage from "../../pages/AnonymousPage";
import ChatroomsListPage from "../../pages/ChatroomsListPage";
import ConfirmResetPage from "../../pages/ConfirmResetPage";
import ConsumerHeatmapPage from "../../pages/ConsumerHeatmapPage";
import CustomerDataPage from "../../pages/CustomerDataPage";
import DailyReportsPage from "../../pages/DailyReportsPage";
import OrderEditPage from "../../pages/EditOrderPage";
import HistoricalOrderDetailPage from "../../pages/HistoricalOrderDetailPage";
import InvoicePage from "../../pages/InvoiceListPage";
import LoginPage from "../../pages/LoginPage";
import OrderSupportPage from "../../pages/OrderSupportPage";
import OrdersHistoryPage from "../../pages/OrdersHistoryPage";
import RefundStatusPage from "../../pages/RefundStatusPage";
import RegisterPage from "../../pages/RegisterPage";
import ResetPage from "../../pages/ResetPage";
import RestaurantConfigurationPage from "../../pages/RestaurantConfigurationPage";
import SettingsPage from "../../pages/SettingsPage";
import SingleChatroomPage from "../../pages/SingleChatroomPage";
import useUser from "../../store/user";

export { default as history } from "./history";

const RequiredLoggedIn = ({ children }) => {
  const { user } = useUser();

	if (user) {
		if (user.isAnonymous) {
			return <Navigate to="/anonymous" />;
		}
		return children;
	}

  return <Navigate to="/login" />;
};

function Router(): React.ReactElement {
  const { user } = useUser();
  const { producerKey } = user || {};

  const canChatSupport = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_CHAT_SUPPORT');
  const canViewOrders = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_VIEW_ORDER');
  const canEditOrders = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_EDIT_ORDER');
  const canViewRefunds = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_REQUEST_REFUNDS');
  const canViewInvoices = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_VIEW_INVOICES');
  const canViewDailyReports = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_VIEW_DAILY_REPORTS') && !!producerKey;
  const canViewCustomerData = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_VIEW_CUSTOMER_DATA') && !!producerKey;;
  const canConfigureRestaurants = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_CONFIGURE_RESTAURANTS');

  return (
		<Routes>
			<Route
				path="/"
				element={
					user ? (
						canViewOrders ? <Navigate to="/active-orders" /> : <Navigate to="/settings" />
					) : (
						<Navigate to="/login" />
					)
				}
			/>

			<Route
				path="/login"
				element={
					user ? <Navigate to="/" /> : <LoginPage />
				}
			/>

      <Route
        path="/password-reset"
        element={
          <ResetPage />
        }
      />

      <Route
        path="/confirm-password-reset/:email"
        element={
          <ConfirmResetPage />
        }
      />

      <Route
        path="/register/:inviteKey"
        element={
          <RegisterPage />
        }
      />

      <Route
        path="/chatrooms"
        element={
          <RequiredLoggedIn>
            <ChatroomsListPage />
          </RequiredLoggedIn>
        }
      />

			<Route
				path="/register/:inviteKey"
				element={
				<RegisterPage />
				}
			/>

			{canChatSupport && <Route
				path="/chatrooms"
				element={
				<RequiredLoggedIn>
					<ChatroomsListPage />
				</RequiredLoggedIn>
				}
			/>}

			{canChatSupport && <Route
				path="/chatrooms/:chatroomKey"
				element={
					<RequiredLoggedIn>
						<SingleChatroomPage />
					</RequiredLoggedIn>
				}
			/>}

			{canViewOrders && <Route
				path="/active-orders"
				element={
					<RequiredLoggedIn>
						<ActiveOrdersPage />
					</RequiredLoggedIn>
				}
			/>}

			{canViewOrders && <Route
				path="/active-orders/:orderKey"
				element={
					<RequiredLoggedIn>
						<ActiveOrderDetailPage />
					</RequiredLoggedIn>
				}
			/>}

			{canEditOrders && <Route
				path="/active-orders/:orderKey/edit"
				element={
					<RequiredLoggedIn>
						<OrderEditPage />
					</RequiredLoggedIn>
				}
			/>}

			{canChatSupport && <Route
				path="/active-orders/:orderKey/support"
				element={
					user?.isAnonymous ? (
						<OrderSupportPage />
					) : (
						<RequiredLoggedIn>
							<OrderSupportPage />
						</RequiredLoggedIn>
					)
				}
			/>}

			{canViewOrders && <Route
				path="/order-history/:orderKey"
				element={
					<RequiredLoggedIn>
						<HistoricalOrderDetailPage />
					</RequiredLoggedIn>
				}
			/>}

			{canEditOrders && <Route
				path="/order-history/:orderKey/edit"
				element={
					<RequiredLoggedIn>
						<OrderEditPage />
					</RequiredLoggedIn>
				}
			/>}

			{canChatSupport && <Route
				path="/order-history/:orderKey/support"
				element={
					<RequiredLoggedIn>
						<OrderSupportPage />
					</RequiredLoggedIn>
				}
			/>}

			{canViewRefunds && <Route
				path="/refunds"
				element={
					<RequiredLoggedIn>
						<RefundStatusPage />
					</RequiredLoggedIn>
				}
			/>}

			{canViewInvoices && <Route
				path="/invoices"
				element={
					<RequiredLoggedIn>
						<InvoicePage/>
					</RequiredLoggedIn>
				}
			/>}

			{canConfigureRestaurants && <Route
				path="/restaurants"
				element={
					<RequiredLoggedIn>
						<RestaurantConfigurationPage/>
					</RequiredLoggedIn>
				}
			/>}

			{canViewDailyReports && <Route
				path="/daily-reports"
				element={
					<RequiredLoggedIn>
						<DailyReportsPage/>
						</RequiredLoggedIn>
				}
			/>}

			{canViewCustomerData && <Route
				path="/customer-data"
				element={
					<RequiredLoggedIn>
						<CustomerDataPage/>
					</RequiredLoggedIn>
				}
			/>}

			{canViewCustomerData && <Route
				path="/consumer-heatmap"
				element={
					<RequiredLoggedIn>
						<ConsumerHeatmapPage/>
					</RequiredLoggedIn>
				}
			/>}

			<Route
				path="/settings"
				element={
					<RequiredLoggedIn>
						<SettingsPage />
					</RequiredLoggedIn>
				}
			/>

      <Route
        path="/order-history"
        element={
          <RequiredLoggedIn>
            <OrdersHistoryPage />
          </RequiredLoggedIn>
        }
      />

			{user?.isAnonymous && <Route
				path="/anonymous"
				element={
          <AnonymousPage />
				}
			/>}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default Router;
